import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { HttpError } from "../../../utils/ajax";
import { updateGTMUserHash } from "../../../utils/analytics";
import { IReduxState } from "../reducers.interfaces";

const FORWARDABLE_ERROR_FIELDS = new Set(["owner_email", "non_field_errors"]);

const isFieldErrorBody = (body: unknown): body is Record<string, string[]> => {
    if (!body || typeof body !== "object" || Array.isArray(body)) {
        return false;
    }
    const entries = Object.entries(body);
    return (
        entries.length > 0 &&
        entries.every(
            ([k, v]) =>
                FORWARDABLE_ERROR_FIELDS.has(k) &&
                Array.isArray(v) &&
                v.length > 0 &&
                v.every((s) => typeof s === "string"),
        )
    );
};

/**
 * Save the guest email address for checkout.
 *
 * This thunk saves the guest email address to the basket/order and updates
 * the Google Tag Manager user hash for analytics tracking. Used during
 * checkout when guest users provide their email address.
 *
 * @returns {Promise<{owner_email: string}>} Promise resolving to the saved email address
 *
 * @example
 * // Save guest email during checkout
 * dispatch(saveEmailAddress());
 *
 * @sideEffects
 * - Reads guest email from `state.form.guest_email`
 * - Calls updateGTMUserHash to update analytics tracking
 * - Makes API call to save email via checkoutAPI.saveEmailAddress
 * - Backend associates email with basket for guest checkout
 * - On an HttpError whose body is field errors for `owner_email` and/or
 *   `non_field_errors` only, rejects with `{response: {body, status}}`,
 *   remapping `owner_email` to `guest_email` so ShippingAddress can show
 *   it inline; every other error body rethrows into the generic-error path
 */
export const saveEmailAddress = createAsyncThunk<
    { owner_email: string },
    void,
    { rejectValue: { response: { body: unknown; status: number } } }
>("checkout/saveEmailAddress", async (_, { getState, rejectWithValue }) => {
    const state = getState() as { checkout: IReduxState };
    const guestEmail = state.checkout.form.guest_email;
    updateGTMUserHash(guestEmail);
    try {
        return await checkoutAPI.saveEmailAddress(guestEmail);
    } catch (err) {
        if (err instanceof HttpError && isFieldErrorBody(err.body)) {
            const { owner_email, ...rest } = err.body;
            const body = owner_email
                ? { ...rest, guest_email: owner_email }
                : err.body;
            return rejectWithValue({
                response: { body, status: err.status },
            });
        }
        throw err;
    }
});
